import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { stringify } from "query-string";
import { matchesBoolean, matchesString } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import { Button, Search } from "MODULES/shared/components";
import {
  FilterSelect,
  Pagination,
  SearchFilter,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import { renderIf } from "MODULES/shared/decorators";
import { ADMIN } from "MODULES/users/constants";
import { useCurrentUser } from "APP_ROOT/hooks";
import { API_BASE, CLEAR_FILTERS_TEXT } from "APP_ROOT/constants";
import { WARRANTY_CONSIDERATION } from "MODULES/RepairOrders/constants";
import { toastr } from "react-redux-toastr";
import api from "APP_ROOT/api";
import Sum from "MODULES/SalesOrders/components/Search/Sum";
import Table from "./Table";
import { salesOrdersPagination } from "../../pagination";

const defaultFilters = {
  brokered: undefined,
  cancelled: undefined,
  consignment: undefined,
  cores_received: undefined,
  current_user: undefined,
  current_user_commissions: undefined,
  customer_rma: undefined,
  exchange_sale: undefined,
  exchange_converted_to_outright: undefined,
  freight_not_complete: undefined,
  invoice_unpaid: undefined,
  qc_hold: undefined,
  shipped: undefined,
  not_shipped: undefined,
  show_invoiced: undefined,
  warranty_consideration: undefined,
  outright_sale: undefined,
  denied_party_hold: undefined,
  new_company_holed: undefined,
  store_id: undefined
};

const baseFilters = [
  {
    label: "My SOs",
    filters: { current_user: true }
  },
  {
    label: "My Commissions",
    filters: { current_user_commissions: true }
  },
  {
    label: "Exchanges",
    filters: { exchange_sale: true }
  },
  {
    label: "Brokered",
    filters: { brokered: true }
  },
  {
    label: "Consignments",
    filters: { consignment: true }
  },
  {
    label: "Core Repair Invoices Not Paid",
    filters: { exchange_sale: true, core_repair_invoices_not_paid: true }
  },
  {
    label: "Denied Parties Hold",
    filters: { denied_party_hold: true }
  },
  {
    label: "New Company Hold",
    filters: { new_company_hold: true }
  },
  {
    label: "Core Repair Inv # Missing",
    filters: {
      exchange_sales: true,
      cores_received: true,
      core_repair_invoice_missing: true
    }
  },
  {
    label: "Customer Invoices Not Paid",
    filters: { invoice_unpaid: true }
  },
  {
    label: "Internal Outright",
    filters: { internal_outright: true }
  },
  {
    label: "Exchange Converted to Outright",
    filters: { exchange_converted_to_outright: true }
  },
  {
    label: "Exchange Gone Outright",
    filters: { outright_sale: true }
  },
  {
    label: "Final Freight Not Confirmed",
    filters: { freight_not_complete: true }
  },
  {
    label: "Invoiced",
    filters: { show_invoiced: true }
  },
  {
    label: "Not Invoiced",
    filters: { show_invoiced: false }
  },
  {
    label: "Synced with Accounting",
    filters: {
      accounting_synced: true
    }
  },
  {
    label: "Not Synced with Accounting",
    filters: {
      accounting_synced: false
    }
  },
  {
    label: "Not Paid",
    filters: { not_paid: true }
  },
  {
    label: "Cores Received",
    filters: { cores_received: true, exchange_sale: true }
  },
  {
    label: "Cores Not Received",
    filters: { cores_received: false, exchange_sale: true }
  },
  {
    label: "Accounting Hold",
    filters: { accounting_hold: true, cancelled: false }
  },
  {
    label: "Cancelled",
    filters: { cancelled: true }
  },
  {
    label: "Not Cancelled",
    filters: { cancelled: false }
  },
  {
    label: "QC Hold",
    filters: { qc_hold: true }
  },
  {
    label: "Shipped",
    filters: { shipped: true }
  },
  {
    label: "Not Shipped",
    filters: { not_shipped: true }
  },
  {
    label: "Cust Rma",
    filters: { customer_rma: true }
  },
  {
    label: "No Cust Rma",
    filters: { customer_rma: false }
  },
  {
    label: "Export Compliance Hold",
    filters: { export_compliance_hold: true }
  },
  {
    label: "Warnings Only",
    filters: { warnings: true }
  },
  {
    label: "Warranty Returns",
    filters: { warranty_consideration: true },
    hr: true
  }
];

const baseFilterTags = [
  {
    label: "Accounting Hold",
    key: "accounting_hold"
  },
  {
    label: "Accounting Synced",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Brokered",
    key: "brokered"
  },
  {
    label: "Not Synced w/ Accounting",
    key: "accounting_synced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Core Repair Invoices Not Paid",
    key: "core_repair_invoices_not_paid"
  },
  {
    label: "Core Repair Inv # Missing",
    key: "core_repair_invoice_missing"
  },
  {
    label: "Customer Invoices Not Paid",
    key: "invoice_unpaid",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Denied Parties Hold",
    key: "denied_party_hold",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "New Company Hold",
    key: "new_company_hold",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "My Sales Orders",
    key: "current_user"
  },
  {
    label: "Consignments",
    key: "consignment"
  },
  {
    label: "My Commissions",
    key: "current_user_commissions"
  },
  {
    label: "Exchanges",
    key: "exchange_sale"
  },
  {
    label: "Internal Outright",
    key: "internal_outright"
  },
  {
    label: "Exchange Converted to Outright",
    key: "exchange_converted_to_outright",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Exchange Gone Outright",
    key: "outright_sale"
  },
  {
    label: "Final Freight Not Confirmed",
    key: "freight_not_complete",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Invoiced",
    key: "show_invoiced",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Invoiced",
    key: "show_invoiced",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Not Paid",
    key: "not_paid",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Cores Received",
    key: "cores_received",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Cores Not Received",
    key: "cores_received",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Cancelled",
    key: "cancelled",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Shipped",
    key: "shipped",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Shipped",
    key: "not_shipped",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "SOs on QC Hold",
    key: "qc_hold",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "SOs on Export Compliance Hold",
    key: "export_compliance_hold"
  },
  {
    label: "Warnings Only",
    key: "warnings"
  },
  {
    label: "Not Cancelled",
    key: "cancelled",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Cust RMA",
    key: "customer_rma",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Cust RMA",
    key: "customer_rma",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Warranty Returns",
    key: WARRANTY_CONSIDERATION
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const totalCount = searchProps.paginator.get("totalCount");

  params.total_count = totalCount;
  return (
    <Button
      onClick={() => {
        if (totalCount > 300) {
          toastr.info("Processing");
          api.salesOrders.lineItems.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/sales_order_line_items/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Sales Order Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(
  ({ searchProps, currentUser }) =>
    searchProps.paginator.get("totalCount") <= 450 || currentUser.role === ADMIN
);

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function SalesOrdersSearch({ pushHistory }) {
  const currentUser = useCurrentUser();

  const locationFilters = [];
  const locationFilterTags = [];

  if (currentUser.locations && currentUser.locations.length > 1) {
    currentUser.locations.forEach((loc, index) => {
      locationFilters.push({
        label: `All Owned by ${loc.name}`,
        filters: {
          ...defaultFilters,
          store_id: loc.id
        }
      });

      locationFilters.push({
        label: `Open SOs Owned by ${loc.name}`,
        filters: {
          ...defaultFilters,
          store_id: loc.id,
          shipped: false,
          cancelled: false
        },
        hr: index === currentUser.locations.length - 1
      });

      locationFilterTags.push({
        label: `Owned by ${loc.name}`,
        key: "store_id",
        matchesTarget: matchesString(loc.id)
      });
    });
  }

  const allFilters = [
    ...baseFilters,
    ...locationFilters,
    {
      label: CLEAR_FILTERS_TEXT,
      filters: defaultFilters
    }
  ];

  const allFilterTags = [...baseFilterTags, ...locationFilterTags];

  return (
    <Search search={salesOrdersPagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a SO #, customer name, customer PO #, invoice #, consignment ref# or seller..."
                submitOnSingleResult={r =>
                  pushHistory(`/sales-orders/${r.sales_order_id}`)}
              />
              <DecoratedExportButton
                searchProps={searchProps}
                currentUser={currentUser}
              />
              <DateRangePicker {...searchProps} />
              <FilterSelect {...searchProps} filters={allFilters} right />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Sales Orders"
            filters={allFilterTags}
            aggregateField="subtotal"
            Sum={Sum}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

SalesOrdersSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(SalesOrdersSearch);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Search/Search.jsx